// Turning a provider's failure text into something a seller can read.
//
// Topup endpoints answer with whatever the upstream operator said, in English,
// and the UI has been showing it verbatim - so an Arabic or Hebrew seller gets
// an English sentence when a topup fails. The messages are free text and carry
// a transaction id ("Topup Failed: Subscriber not found (tx=1499670)"), so they
// cannot be used as translation keys directly; each is recognised by a fragment
// instead.
//
// Anything unrecognised falls through to the original text. A seller seeing the
// provider's own words is worse than a translation, but far better than a blank
// toast or a key name - and the untranslated string is the thing that tells us
// which pattern to add next.

const REASON_PATTERNS = [
  // provider refusals
  { match: /subscriber not found/i, key: "reason_subscriber_not_found" },
  { match: /is inactive/i, key: "reason_product_inactive" },
  { match: /insufficient|not enough balance/i, key: "reason_provider_no_balance" },
  { match: /invalid (phone|number|msisdn)/i, key: "reason_invalid_number" },

  // the operator's session lapsed - their token lasts a day and a topup was
  // attempted before the new code had been entered
  { match: /session expired|validation error|transaction create http 4/i,
    key: "reason_provider_session" },

  // cancellations
  { match: /cancel(l)?ation? failed|cancel failed/i, key: "reason_cancel_failed" },
  { match: /already cancel/i, key: "reason_already_cancelled" },
  { match: /no provider reference/i, key: "reason_no_provider_reference" },
  { match: /rollback refused/i, key: "reason_cancel_failed" },
  { match: /cancel request posted/i, key: "reason_cancel_requested" },
  { match: /can not cancel the same number/i, key: "reason_cancel_same_number" },
  { match: /transaction already posted/i, key: "reason_cancel_already_posted" },
  { match: /status NOT success/i, key: "reason_cancel_not_successful" },

  // our own states
  { match: /^seller no balance$/i, key: "reason_seller_no_balance" },
  { match: /provider unreachable/i, key: "reason_provider_unreachable" },
  { match: /awaiting confirmation/i, key: "reason_awaiting_confirmation" },
  { match: /duplicate blocked/i, key: "reason_duplicate_blocked" },
  { match: /missing parameters/i, key: "reason_missing_parameters" },
  { match: /token expired/i, key: "reason_token_expired" },
  { match: /wrong card/i, key: "reason_wrong_card" },
  { match: /^success$/i, key: "reason_success" },
];

export const providerReasonKey = (reason) => {
  const text = String(reason || "").trim();
  if (!text) return undefined;

  const found = REASON_PATTERNS.find(({ match }) => match.test(text));
  return found && found.key;
};

// `intl` is the react-intl object; passing it in keeps this a plain function
// that any action or component can call.
export const translateProviderReason = (intl, reason) => {
  const text = String(reason || "").trim();
  const key = providerReasonKey(text);

  if (!key) return text;

  // defaultMessage keeps the provider's own words if the key is ever missing
  // from a language file, rather than rendering the key itself.
  return intl.formatMessage({ id: key, defaultMessage: text });
};

export default translateProviderReason;
